import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveNsxReviews,
  createContact,
  getDsgBinaryLatest,
  getDsgEcrToken,
  getLoggedUser,
  getNetworkDetails,
  getNsxReviews,
  getOrganisationDetails,
  getOrgContacts,
  getRcsCognitoClientSecret,
  getRcsOnboardingInfo,
  postRegisterUser,
  putNetworkDetailsRcsVerified,
  putOrganisationUpgrade,
  rejectNsxReviews,
  updateContact,
  updateUser,
} from '../../services';

export const NSX_REVIEWS_QUERY_KEY = ['get_nsx_reviews'];
export const NETWORK_DETAILS_QUERY_KEY = ['get_network_details'];
export const RCS_ONBOARDING_QUERY_KEY = ['get_rcs_onboarding_info'];

export const useRegisterUser = () => {
  return useMutation({
    mutationKey: ['register_user'],
    mutationFn: postRegisterUser,
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationKey: ['update_user'],
    mutationFn: updateUser,
  });
};

export const useGetOrganisationDetailsById = (id: string) => {
  return useQuery({
    queryKey: ['organisation_details', id],
    queryFn: () =>
      getOrganisationDetails({ organisation_id: id, silent: false }),
    enabled: !!id,
  });
};

export const useAsyncGetOrganisationDetailsById = () => {
  return useMutation({
    mutationKey: ['get_organisation_details'],
    mutationFn: getOrganisationDetails,
  });
};

export const useOrganisationUpgrade = () => {
  return useMutation({
    mutationKey: ['organisation_upgrade'],
    mutationFn: putOrganisationUpgrade,
  });
};

export const useGetCurrentUser = () => {
  return useMutation({
    mutationKey: ['get_current_user'],
    mutationFn: getLoggedUser,
  });
};

export const useGetCurrentUserQuery = () => {
  return useQuery({
    queryKey: ['get_current_user'],
    queryFn: () => getLoggedUser({ silent: false }),
  });
};

export const useGetOrgContacts = () => {
  return useQuery({
    queryKey: ['get_org_contacts'],
    queryFn: getOrgContacts,
  });
};

export const useAsyncGetOrgContacts = () => {
  return useMutation({
    mutationKey: ['get_async_org_contacts'],
    mutationFn: getOrgContacts,
  });
};

export const useUpdateContact = () => {
  return useMutation({
    mutationKey: ['update_contact'],
    mutationFn: updateContact,
  });
};

export const useCreateContact = () => {
  return useMutation({
    mutationKey: ['create_contact'],
    mutationFn: createContact,
  });
};

export const useGetNetworkDetails = () => {
  return useQuery({
    queryKey: NETWORK_DETAILS_QUERY_KEY,
    queryFn: getNetworkDetails,
  });
};

// The flag is projected into both the network details list and the NSX
// review view, so both caches are stale after a change.
export const useSetNetworkDetailsRcsVerified = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['set_network_details_rcs_verified'],
    mutationFn: putNetworkDetailsRcsVerified,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NETWORK_DETAILS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NSX_REVIEWS_QUERY_KEY });
    },
  });
};

export const useGetRcsOnboardingInfo = () => {
  return useQuery({
    queryKey: RCS_ONBOARDING_QUERY_KEY,
    queryFn: getRcsOnboardingInfo,
    refetchOnWindowFocus: false,
  });
};

/**
 * Deliberately a mutation, not a query: the client secret must not be cached,
 * refetched in the background, or shared through the query cache — it is read
 * once, on an explicit user action, and kept only in component state.
 */
export const useGetRcsCognitoClientSecret = () => {
  return useMutation({
    mutationKey: ['rcs_cognito_client_secret'],
    mutationFn: getRcsCognitoClientSecret,
  });
};

export const useGetNsxReviews = () => {
  return useQuery({
    queryKey: NSX_REVIEWS_QUERY_KEY,
    queryFn: getNsxReviews,
  });
};

// Approve/reject return the refreshed list, so the cache is replaced from the
// response instead of refetching.
export const useApproveNsxReviews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['approve_nsx_reviews'],
    mutationFn: approveNsxReviews,
    onSuccess: data => {
      queryClient.setQueryData(NSX_REVIEWS_QUERY_KEY, data);
    },
  });
};

export const useRejectNsxReviews = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['reject_nsx_reviews'],
    mutationFn: rejectNsxReviews,
    onSuccess: data => {
      queryClient.setQueryData(NSX_REVIEWS_QUERY_KEY, data);
    },
  });
};

export const useGetDsgBinaryLatest = () => {
  return useMutation({
    mutationKey: ['dsg_binary_latest'],
    mutationFn: getDsgBinaryLatest,
  });
};

export const useGetDsgEcrToken = () => {
  return useMutation({
    mutationKey: ['dsg_ecr_token'],
    mutationFn: getDsgEcrToken,
  });
};
